import React, { useState, useCallback, useEffect, useRef, useId } from 'react';
import { Grow, Position, Width } from './types';
import * as S from './styles';
import { IconClose } from '@prisjakt/ui/icon';

type OwnProps = {
  toggleElement: React.ReactElement;
  toggleElementStyles?: {};
  toggleElementActiveStyles?: {};
  style?: {};
  position?: Position;
  grow?: Grow;
  width?: Width;
  showCloseButton?: boolean;
  closeOnClick?: boolean;
  className?: string;
};

const Menu: React.FC<React.PropsWithChildren<OwnProps>> = ({
  children,
  toggleElement,
  toggleElementStyles,
  toggleElementActiveStyles,
  className,
  style,
  position = 'bottom',
  grow = 'right',
  width = 'auto',
  showCloseButton = false,
  closeOnClick = false,
}) => {
  const [visible, setVisibilityState] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Generate unique UIDs to use as HTML ids
  const getId = useId();
  const dropdownId = `${getId}-menu-dropdown`;

  const toggleMenu = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      setVisibilityState(!visible);
    },
    [visible]
  );

  const handleClick = useCallback(
    (e: MouseEvent) => {
      const clickedMenubutton = menuRef.current?.contains(e.target as Node);

      if (closeOnClick || !clickedMenubutton) {
        setVisibilityState(false);
      }
    },
    [closeOnClick, setVisibilityState, menuRef]
  );

  useEffect(() => {
    if (visible) {
      document.addEventListener('click', handleClick);
    }

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [visible, handleClick]);

  if (!toggleElement) return null;

  const styles = {
    ...toggleElementStyles,
    ...(visible && toggleElementActiveStyles),
  };

  return (
    <S.Menu ref={menuRef} className={className} style={style}>
      <toggleElement.type
        style={styles}
        onClick={toggleMenu}
        {...toggleElement.props}
        aria-expanded={visible}
        aria-haspopup="true"
        aria-controls={dropdownId}
        data-test="MenuToggleElement"
      />

      <S.MenuDropdown
        visible={visible}
        width={width}
        position={position}
        grow={grow}
        containerProps={{
          id: dropdownId,
          role: 'menu',
        }}
      >
        {showCloseButton && (
          <S.CloseButton onClick={toggleMenu}>
            <IconClose color="base300" />
          </S.CloseButton>
        )}
        {children}
      </S.MenuDropdown>
    </S.Menu>
  );
};

export default Menu;
